import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatDistanceToNowStrict } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date to IST timezone
 * Uses DD MMM YYYY format (e.g., "27 Nov 2025")
 */
export function formatDate(date: string | Date): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date:', date);
    return '';
  }

  // Format to DD MMM YYYY in IST timezone
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata', // IST timezone
  };

  return dateObj.toLocaleDateString('en-IN', options);
}

/**
 * Format a date with time to IST timezone
 * Uses DD MMM YYYY, hh:mm AM/PM format (e.g., "27 Nov 2025, 02:30 PM")
 */
export function formatDateTime(date: string | Date): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date:', date);
    return '';
  }

  // Format to DD MMM YYYY, hh:mm AM/PM in IST timezone
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata', // IST timezone
  };

  return dateObj.toLocaleString('en-IN', options);
}

/**
 * Format just the time in IST timezone
 * Uses hh:mm AM/PM format (e.g., "02:30 PM")
 */
export function formatTime(date: string | Date): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date:', date);
    return '';
  }

  // Format to hh:mm AM/PM in IST timezone
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata', // IST timezone
  };

  return dateObj.toLocaleTimeString('en-IN', options);
}

export function formatDistanceToNow(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const isPast = dateObj < now;

  return formatDistanceToNowStrict(dateObj, { addSuffix: true });
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
}

export function getProgressColor(progress: number): string {
  if (progress >= 100) return 'bg-success';
  if (progress >= 75) return 'bg-primary';
  if (progress >= 50) return 'bg-yellow-500';
  if (progress >= 25) return 'bg-orange-500';
  return 'bg-destructive';
}